import type { LocalBackup } from './local-backup';
import { validateBackup } from './restore-backup';

const MAX_BYTES = 50_000_000;
const tooLarge = () => new Error('Копия слишком большая для безопасного восстановления.');

function assertJsonSafe(value:unknown, seen=new WeakSet<object>()):void {
  if(value===null || typeof value==='string' || typeof value==='boolean')return;
  if(typeof value==='number' && Number.isFinite(value) && !Object.is(value,-0))return;
  if(!value || typeof value!=='object' || seen.has(value))throw new Error('Некоторые данные нельзя безопасно сохранить в копии.');
  if(!Array.isArray(value) && ![Object.prototype,null].includes(Object.getPrototypeOf(value)))throw new Error('Некоторые данные нельзя безопасно сохранить в копии.');
  seen.add(value);
  if(Array.isArray(value)){for(let i=0;i<value.length;i++){if(!Object.hasOwn(value,i))throw new Error('Некоторые данные нельзя безопасно сохранить в копии.');assertJsonSafe(value[i],seen);}}
  else for(const key of Object.keys(value))assertJsonSafe((value as Record<string,unknown>)[key],seen);
  seen.delete(value);
}
export function assertBackupSnapshotIsSafe(snapshot:LocalBackup):void {validateBackup(snapshot);assertJsonSafe(snapshot);}

async function readLimited(stream: ReadableStream<Uint8Array>): Promise<Uint8Array> {
  const reader=stream.getReader(); const chunks:Uint8Array[]=[]; let size=0;
  try {
    for (;;) {
      const {done,value}=await reader.read(); if(done)break;
      size+=value.byteLength;
      if(size>MAX_BYTES){await reader.cancel();throw tooLarge();}
      chunks.push(value);
    }
  } finally {reader.releaseLock();}
  const all=new Uint8Array(size);let offset=0;for(const chunk of chunks){all.set(chunk,offset);offset+=chunk.byteLength;}return all;
}

async function unpackGzip(blob:Blob):Promise<string> {
  if(typeof DecompressionStream==='undefined')throw new Error('Этот браузер не поддерживает открытие сжатой копии.');
  const data=await readLimited(blob.stream().pipeThrough(new DecompressionStream('gzip')));
  return new TextDecoder('utf-8',{fatal:true}).decode(data);
}

/** Compress losslessly only when the same device can verify the full round trip. */
export async function createBackupFile(snapshot:LocalBackup):Promise<{blob:Blob;compressed:boolean}> {
  assertBackupSnapshotIsSafe(snapshot);
  const json=JSON.stringify(snapshot);
  const original=new Blob([json],{type:'application/json'});
  if(original.size>MAX_BYTES)throw tooLarge();
  if(typeof CompressionStream==='undefined' || typeof DecompressionStream==='undefined')return {blob:original,compressed:false};
  try {
    const buffer=await new Response(original.stream().pipeThrough(new CompressionStream('gzip'))).arrayBuffer();
    const blob=new Blob([buffer],{type:'application/gzip'});
    if(blob.size>=original.size || await unpackGzip(blob)!==json)return {blob:original,compressed:false};
    return {blob,compressed:true};
  } catch { return {blob:original,compressed:false}; }
}

/** Supports both existing JSON backups and new gzip files; never writes data. */
export async function readBackupFile(file:Blob):Promise<{backup:LocalBackup;compressed:boolean}> {
  if(file.size>MAX_BYTES)throw tooLarge();
  const magic=new Uint8Array(await file.slice(0,2).arrayBuffer());
  const compressed=magic[0]===0x1f && magic[1]===0x8b;
  let json:string;
  try { json=compressed ? await unpackGzip(file) : await file.text(); }
  catch(error) { if(error instanceof Error && (error.message.includes('слишком большая') || error.message.includes('не поддерживает')))throw error;throw new Error('Не удалось прочитать резервную копию.'); }
  let data:unknown;
  try {data=JSON.parse(json);} catch {throw new Error('Неверный формат резервной копии.');}
  validateBackup(data);return {backup:data,compressed};
}
